import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React from "react";
import {Controller, useFormContext} from "react-hook-form";
import MWCaption from "../../components/MWCaption";

import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import DeleteIcon from "@mui/icons-material/Delete";
import {Box} from "@mui/system";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import MWInputFile from "../../components/MWInputFile";
import {useConvertBase64} from "../../hooks/useConvertBase64";
import {useDepartmentList} from "../../hooks/useDepartmentList";
import {EQRevisionDocument} from "../../models/serverModels/workOrder/EQRevisionDocument";
import ko from "date-fns/locale/ko";

const CategoryFormContainer = styled(Grid)(({theme}) => ({
  justifyContent: "center",
  alignItems: "center",
  marginTop: "16px",

  backgroundColor: theme.palette.gray.main,
}));

const CategoryFormContent = styled(Grid)(({theme}) => ({
  display: "flex",
  flexFlow: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  margin: "32px 0",

  padding: "24px",
  backgroundColor: theme.palette.gray.dark,

  border: `1px solid ${theme.palette.background.paper}`,
  borderRadius: "16px",
  boxShadow:
    "rgb(0 0 0 / 10%) 0rem 0.25rem 0.375rem -0.0625rem, rgb(0 0 0 / 6%) 0rem 0.125rem 0.25rem -0.0625rem",

  "& label": {
    fontWeight: 600,
    paddingRight: "8px",
  },
}));

const DocumentList = styled(Box)(({theme}) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  "& label": {
    flexBasis: "30%",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));
type DocumentProps = {
  subText: string;
  captionColor?: string;
};

const RevisionDocumentForm = ({subText, captionColor}: DocumentProps) => {
  const {convertChangeHandler, image} = useConvertBase64();
  const {departmentList} = useDepartmentList();
  const isMobile = useMediaQuery("(max-width: 900px)");

  const methods = useFormContext<EQRevisionDocument>();

  const {
    setValue,
    getValues,
    control,
    watch,
    formState: {errors},
  } = methods;
  return (
    <React.Fragment>
      <MWCaption title={subText} color={captionColor} icon={<ContentPasteSearchIcon />} />
      <CategoryFormContainer container>
        <CategoryFormContent
          item
          xs={12}
          sm={12}
          md={6}
          xl={4}
          sx={{padding: {xs: "16px 4px", sm: "16px 8px", md: "24px"}}}
        >
          <Controller
            name="name"
            control={control}
            render={({field, fieldState: {error}}) => (
              <DocumentList>
                <label htmlFor="name">보고서 명 : </label>
                <TextField
                  {...field}
                  id="name"
                  size="small"
                  placeholder="보고서 명"
                  margin="dense"
                  autoFocus
                  helperText={error?.message}
                  sx={{width: "100%"}}
                />
              </DocumentList>
            )}
          />
          <Controller
            name="departmentUID"
            control={control}
            render={({field}) => (
              <DocumentList>
                <label htmlFor="departmentUID">부서 : </label>

                <Select
                  {...field}
                  id="departmentUID"
                  size="small"
                  margin="dense"
                  style={{width: "100%"}}
                >
                  <MenuItem disabled value={field.value} style={{display: "none"}}>
                    선택하세요
                  </MenuItem>
                  {departmentList?.items! !== undefined &&
                    departmentList?.items!.map((item, index) => (
                      <MenuItem key={item.uid} value={item.uid}>
                        {item.name}
                      </MenuItem>
                    ))}
                </Select>
              </DocumentList>
            )}
          />
          <Controller
            name="serialNo"
            control={control}
            render={({field, fieldState: {error}}) => (
              <DocumentList>
                <label htmlFor="revisionNo">Part No : </label>
                <TextField
                  {...field}
                  id="revisionNo"
                  size="small"
                  placeholder="Part No"
                  margin="dense"
                  helperText={error?.message}
                  sx={{width: "100%", color: "#000"}}
                />
              </DocumentList>
            )}
          />
          <Controller
            name="firstDate"
            control={control}
            render={({field, ...props}) => (
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                <DocumentList>
                  <label htmlFor="firstDate">최초작성일 : </label>
                  <DatePicker
                    inputFormat={"yyyy년 MM월 dd일"}
                    value={field.value}
                    onChange={(date) => {
                      if (!date) return field.onChange(null);

                      const d = new Date(date); // string 가능 → 자동 파싱됨

                      const fixed = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0, 0);

                      field.onChange(fixed);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="firstDate"
                        size="small"
                        margin="dense"
                        sx={{width: "100%"}}
                      />
                    )}
                  />
                </DocumentList>
              </LocalizationProvider>
            )}
          />
          <Controller
            name="lastDate"
            control={control}
            render={({field, ...props}) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DocumentList>
                  <label htmlFor="lastDate">최근개정일 : </label>
                  <DatePicker
                    inputFormat={"yyyy년 MM월 dd일"}
                    value={field.value}
                    onChange={(date) => {
                      if (!date) return field.onChange(null);

                      const d = new Date(date); // string 가능 → 자동 파싱됨

                      const fixed = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0, 0);

                      field.onChange(fixed);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="lastDate"
                        size="small"
                        margin="dense"
                        sx={{width: "100%"}}
                      />
                    )}
                  />
                </DocumentList>
              </LocalizationProvider>
            )}
          />
          <Controller
            name="revisionNo"
            control={control}
            render={({field, fieldState: {error}}) => (
              <DocumentList>
                <label htmlFor="revisionNo">개정번호 : </label>
                <TextField
                  {...field}
                  id="revisionNo"
                  size="small"
                  placeholder="개정번호"
                  margin="dense"
                  sx={{width: "100%"}}
                  helperText={error?.message}
                />
              </DocumentList>
            )}
          />
          <Controller
            name="extra"
            control={control}
            render={({field, fieldState: {error}}) => (
              <DocumentList>
                <label htmlFor="extra">비고 : </label>
                <TextField
                  {...field}
                  id="extra"
                  size="small"
                  placeholder="비고"
                  margin="dense"
                  sx={{width: "100%"}}
                  helperText={error?.message}
                />
              </DocumentList>
            )}
          />
          {isMobile ? (
            <Box sx={{padding: "8px 4px", width: "100%"}}>
              <MWInputFile file="base64" fileName="fileName" buttonLabel="서식" />
            </Box>
          ) : (
            <Controller
              name="base64"
              control={control}
              render={({field: {onChange, name, value}, fieldState: {error}}) => (
                <>
                  <Grid
                    container
                    sx={{
                      marginTop: "12px",
                      flexWrap: "nowrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{display: "flex", alignItems: "center"}}>
                      <label htmlFor="base64" style={{whiteSpace: "nowrap"}}>
                        서식 :
                      </label>
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        sx={{height: "30px", whiteSpace: "nowrap"}}
                      >
                        파일 선택
                        <TextField
                          sx={{display: "none"}}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            convertChangeHandler(event, setValue, name, "fileName")
                          }
                          type="file"
                          id="base64"
                          size="small"
                          hidden
                        />
                      </Button>
                      <Typography align="left" sx={{marginLeft: "8px", marginTop: 0}}>
                        {watch("fileName")}
                      </Typography>
                    </Box>

                    <IconButton
                      aria-label="delete"
                      color="secondary"
                      onClick={() => {
                        setValue("base64", "");
                        setValue("fileName", "");
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  {errors && (
                    <Typography sx={{fontSize: "12px", color: "red"}}>{error?.message}</Typography>
                  )}
                </>
              )}
            />
          )}
          {getValues("type") === 1 ? null : (
            <>
              {isMobile ? (
                <Box sx={{padding: "8px 4px", width: "100%"}}>
                  <MWInputFile
                    file="addtional1Base64"
                    fileName="additionalFile1"
                    buttonLabel="추가첨부1"
                  />
                </Box>
              ) : (
                <Controller
                  name="addtional1Base64"
                  control={control}
                  render={({field: {onChange, name, value}, fieldState: {error}}) => (
                    <>
                      <Grid
                        container
                        sx={{
                          marginTop: "12px",
                          flexWrap: "nowrap",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box sx={{display: "flex", alignItems: "center"}}>
                          <label htmlFor="addtional1Base64" style={{whiteSpace: "nowrap"}}>
                            추가첨부1 :{" "}
                          </label>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{height: "30px", whiteSpace: "nowrap"}}
                          >
                            파일 선택
                            <TextField
                              sx={{display: "none"}}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                convertChangeHandler(event, setValue, name, "additionalFile1")
                              }
                              type="file"
                              id="addtional1Base64"
                              size="small"
                              hidden
                            />
                          </Button>
                          <Typography align="left" sx={{marginLeft: "8px"}}>
                            {watch("additionalFile1")}
                          </Typography>
                        </Box>
                        <IconButton
                          aria-label="delete"
                          color="secondary"
                          onClick={() => {
                            setValue("addtional1Base64", "");
                            setValue("additionalFile1", "");
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {errors.addtional1Base64 && (
                          <Typography>{errors.addtional1Base64.message}</Typography>
                        )}
                      </Grid>
                      {errors && (
                        <Typography sx={{fontSize: "12px", color: "red"}}>
                          {error?.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              )}

              {isMobile ? (
                <Box sx={{padding: "8px 4px", width: "100%"}}>
                  <MWInputFile
                    file="addtional2Base64"
                    fileName="additionalFile2"
                    buttonLabel="추가첨부2"
                  />
                </Box>
              ) : (
                <Controller
                  name="addtional2Base64"
                  control={control}
                  render={({field: {onChange, name, value}, fieldState: {error}}) => (
                    <>
                      <Grid
                        container
                        sx={{
                          marginTop: "12px",
                          flexWrap: "nowrap",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box sx={{display: "flex", alignItems: "center"}}>
                          <label htmlFor="addtional2Base64" style={{whiteSpace: "nowrap"}}>
                            추가첨부2 :{" "}
                          </label>
                          <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            sx={{height: "30px"}}
                          >
                            파일 선택
                            <TextField
                              sx={{display: "none"}}
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                convertChangeHandler(event, setValue, name, "additionalFile2")
                              }
                              type="file"
                              id="addtional2Base64"
                              size="small"
                              hidden
                            />
                          </Button>
                          <Typography align="left" sx={{marginLeft: "8px", whiteSpace: "nowrap"}}>
                            {watch("additionalFile2")}
                          </Typography>
                        </Box>
                        <IconButton
                          aria-label="delete"
                          color="secondary"
                          onClick={() => {
                            setValue("addtional2Base64", "");
                            setValue("additionalFile2", "");
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        {errors.addtional2Base64 && (
                          <Typography>{errors.addtional2Base64.message}</Typography>
                        )}
                      </Grid>
                      {errors && (
                        <Typography sx={{fontSize: "12px", color: "red"}}>
                          {error?.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              )}
            </>
          )}
        </CategoryFormContent>
      </CategoryFormContainer>
    </React.Fragment>
  );
};

export default RevisionDocumentForm;
